import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

interface BookedProduct {
  product_id: string;
  product_type: string;
  date_debut: string;
  date_fin: string;
}

interface ClosurePeriod {
  id: string;
  date_debut: string;
  date_fin: string;
  affected_product_ids: string[];
}

/**
 * Hook to check product availability for given dates.
 * Checks both reservations (status 'reserve') and closure periods.
 */
export function useAvailability(dateDebut?: string, dateFin?: string) {
  const [bookedProducts, setBookedProducts] = useState<BookedProduct[]>([]);
  const [sumoIds, setSumoIds] = useState<string[]>([]);
  const [productQuantities, setProductQuantities] = useState<Record<string, number>>({});
  const [closedProductIds, setClosedProductIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  const checkAvailability = useCallback(async () => {
    if (!dateDebut || !dateFin) {
      setBookedProducts([]);
      setClosedProductIds(new Set());
      return;
    }

    try {
      setLoading(true);

      const [reservationsRes, chateauxRes, sumosRes, closuresRes] = await Promise.all([
        supabase.rpc('get_reserved_slots' as any, {
          p_date_debut: dateDebut,
          p_date_fin: dateFin,
        }),
        supabase
          .from('chateaux')
          .select('id, quantite'),
        supabase
          .from('sumos')
          .select('id, quantite'),
        supabase
          .from('closure_periods' as any)
          .select('id, date_debut, date_fin, affected_product_ids')
          .eq('is_active', true)
          .lte('date_debut', dateFin)
          .gte('date_fin', dateDebut),
      ]);

      if (reservationsRes.error) {
        logger.error('Erreur requête réservations', reservationsRes.error);
        throw reservationsRes.error;
      }
      
      const booked = (reservationsRes.data as any[]) || [];
      setBookedProducts(booked);

      // Build quantity map
      const qMap: Record<string, number> = {};
      if (!chateauxRes.error && chateauxRes.data) {
        for (const c of chateauxRes.data) {
          qMap[c.id] = (c as any).quantite ?? 1;
        }
      }
      if (!sumosRes.error && sumosRes.data) {
        const ids: string[] = [];
        for (const s of sumosRes.data) {
          qMap[s.id] = (s as any).quantite ?? 1;
          ids.push(s.id);
        }
        setSumoIds(ids);
      }
      setProductQuantities(qMap);

      // Build closed products set from closure periods
      const closed = new Set<string>();
      if (!closuresRes.error && closuresRes.data) {
        for (const closure of (closuresRes.data as any[]) as ClosurePeriod[]) {
          for (const pid of closure.affected_product_ids) {
            closed.add(pid);
          }
        }
      }
      setClosedProductIds(closed);
    } catch (error) {
      logger.error('Erreur vérification disponibilité', error);
      setBookedProducts([]);
      setClosedProductIds(new Set());
    } finally {
      setLoading(false);
    }
  }, [dateDebut, dateFin]);

  useEffect(() => {
    checkAvailability();
  }, [checkAvailability]);

  const isProductAvailable = useCallback((productId: string, productType: string = 'chateau') => {
    if (!dateDebut || !dateFin) return true;

    // Check closure periods first
    if (closedProductIds.has(productId)) return false;

    // Count how many confirmed bookings overlap for this product
    const bookedCount = bookedProducts.filter(
      b => b.product_id === productId && b.product_type === productType
    ).length;

    const totalQuantity = productQuantities[productId] ?? 1;
    return bookedCount < totalQuantity;
  }, [bookedProducts, productQuantities, closedProductIds, dateDebut, dateFin]);

  const getAvailableCount = useCallback((productId: string, productType: string = 'chateau') => {
    if (!dateDebut || !dateFin) return undefined;
    
    // If closed, 0 available
    if (closedProductIds.has(productId)) return 0;
    
    const bookedCount = bookedProducts.filter(
      b => b.product_id === productId && b.product_type === productType
    ).length;
    const totalQuantity = productQuantities[productId] ?? 1;
    return Math.max(0, totalQuantity - bookedCount);
  }, [bookedProducts, productQuantities, closedProductIds, dateDebut, dateFin]);

  const isSumoAvailable = useCallback((sumoId?: string) => {
    if (!dateDebut || !dateFin) return true;
    if (sumoId) return isProductAvailable(sumoId, 'sumo');
    // No specific sumo id: at least one sumo must be available (not closed and not fully booked)
    if (sumoIds.length === 0) {
      // Fallback to legacy check if we haven't loaded sumo ids yet
      return !bookedProducts.some(b => b.product_type === 'sumo');
    }
    return sumoIds.some(id => isProductAvailable(id, 'sumo'));
  }, [bookedProducts, dateDebut, dateFin, isProductAvailable, sumoIds]);

  return {
    bookedProducts,
    loading,
    isProductAvailable,
    getAvailableCount,
    isSumoAvailable,
    checkAvailability,
    hasDatesSelected: Boolean(dateDebut && dateFin),
  };
}
